import { Chip, ChipGroup, type SvgComponentType } from "@newsela/angelou";
import { ChangeEvent } from "react";
import { useRouter } from "next/router";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";
import {
  contentTypesChipGroupCss,
  contentTypesVisibilityCss,
  getContentTypeChipCss,
} from "@/search/Langston/FilterPanel/filters/ContentTypes/ContentTypes.styles";
import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { useFilterControls } from "@/search/Langston/useFilterControls";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useContentTypeValues } from "@/search/Langston/LangstonParameterSchema/useContentTypeValues";
import { FilterSection } from "@/search/Langston/FilterPanel/FilterSection";

const WORKSHEET_ENUM = "WORKSHEET";

const ContentTypes = () => {
  const { pathname } = useRouter();
  const isUniversalEmbed = pathname.startsWith("/search/universal-embed");
  const {
    getters: { isDisabledFilters },
    handlers: {
      getSearchParamValues,
      handleWorksheets,
      toggleSearchParamValue,
      deleteSearchParamValues,
    },
  } = useSearchPageContext();

  const { handleClearAllFilters } = useFilterControls();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  // Using a custom tracking instead of withTrackerEvent to avoid accessibility issues and also the withTrackerEvent with Chip component doesn't work properly
  const { trackAction, getEventName } = useTracker();

  const contentTypeValues = useContentTypeValues(isUniversalEmbed);

  if (!contentTypeValues) return null;

  const isWorthsheetsEnabled = isDisabledFilters;

  const handleTrackAction = (value: string) =>
    trackAction({
      event_name: getEventName(
        objectTypes.content,
        actionPrefixes.initiate,
        actionNames.filter,
      ),
      event_type: eventTypes.click,
      dimension_properties: {
        description: `Filter search by ${value} Content Type`,
      },
    });

  const handleChipSelection = (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    if (value === WORKSHEET_ENUM) {
      handleClearAllFilters();
      handleWorksheets(event.target.checked);
      handleTrackAction(value);
      return;
    }

    if (isDisabledFilters && value !== "all") {
      return;
    }

    handleTrackAction(value);

    if (value === "all") {
      if (isDisabledFilters) handleWorksheets(false);
      deleteSearchParamValues(LangstonParameterSchema.content_types.key);
      return;
    }
    toggleSearchParamValue(LangstonParameterSchema.content_types.key, value);
  };

  const activeChips = getSearchParamValues(
    LangstonParameterSchema.content_types.key,
  );
  const isSelectAllChipActive =
    activeChips.length === 0 && !isWorthsheetsEnabled;

  return (
    <div css={contentTypesVisibilityCss}>
      <FilterSection title="Content type" defaultCollapsed={false}>
        <ChipGroup
          groupName="contentTypeLevelFilter"
          __cssFor={contentTypesChipGroupCss}
          onChipEvent={(e, val) => handleChipSelection(e, val)}
          size={ChipGroup.chipSizes.small}
          chipType={ChipGroup.chipTypes.filter}
        >
          {!isUniversalEmbed && (
            <Chip
              __cssFor={getContentTypeChipCss(isSelectAllChipActive)}
              key="select-all-chip"
              dataProps={{
                "data-qa-selector": "select-all-content-types-filter",
                "data-testid": "select-all-content-types-filter",
                "data-is-selected": String(isSelectAllChipActive),
              }}
              active={isSelectAllChipActive}
              text="All"
              value="all"
            />
          )}

          {contentTypeValues.map(({ label, value }) => {
            const isActive =
              activeChips.includes(value) ||
              (value === WORKSHEET_ENUM && isWorthsheetsEnabled);

            const svg = LangstonParameterSchema.content_types.values.find(
              (type) => type.value === value,
            )?.SvgComponent;

            return (
              <Chip
                key={value}
                value={value}
                text={label}
                active={isActive}
                SvgComponent={svg as SvgComponentType}
                __cssFor={getContentTypeChipCss(isActive)}
                dataProps={{
                  "data-qa-selector": `select-${label.replace(" ", "")}-search-filter`,
                  "data-testid": `select-${label.replace(" ", "")}-search-filter`,
                  "data-is-selected": String(isActive),
                }}
              />
            );
          })}
        </ChipGroup>
      </FilterSection>
    </div>
  );
};

ContentTypes.displayName = "ContentTypes";
export { ContentTypes };
